import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from '../../api/axios';
import { toast } from "react-toastify";
import Select from "react-select";
import { TagsInput } from "react-tag-input-component";
import { Button, Col, Form, Row, Spinner, Card, Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import Header from "../../layouts/Header";
import makeAnimated from "react-select/animated";
import { ArrowLeft, Save, X, Image as ImageIcon, FileText } from 'react-bootstrap-icons';

const UpdateMenu = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();
    const { productId } = useParams();
    const { access } = useSelector((state) => state.login);
    const accessToken = localStorage.getItem("authToken");

    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [categoryList, setCategoryList] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [productTags, setProductTags] = useState([]);
    const [productTagsList, setProductTagsList] = useState([]);
    const [selected, setSelected] = useState([]);
    const [selectedNature, setSelectedNature] = useState([]);
    const [editedSelectedExOutlets, setEditedSelectedExOutlets] = useState([]);
    const [tagText, setTagText] = useState("");
    
    const [displayImage, setDisplayImage] = useState(null);
    const [displayImagePreview, setDisplayImagePreview] = useState("");
    const [coverImage, setCoverImage] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState("");
    const [imageError, setImageError] = useState("");
    
    const animatedComponents = makeAnimated();

    const [editedData, setEditedData] = useState({
        _id: "",
        sequenceNumber: "",
        name: "",
        description: "",
        hsn: "",
        taxPercent: "",
        makingTime: "",
        mrp: "",
        status: "",
        category: "",
        offerPrice: "",
        foodType: "",
        classType: "",
        productTagId: "",
        productTagName: "",
        isEventProduct: "",
        isFreebieProduct: "",
        calories: "",
        weight: "",
        weightUnit: "",
        orderType: "",
        isNewProduct: "",
        productCost: "",
        imageURL: "",
        coverImageURL: ""
    });

    useEffect(() => {
        fetchProductDetails();
        fetchCategories();
        fetchOutlets();
        fetchProductTagsList();
    }, [productId]);

    const fetchProductDetails = async () => {
        setIsFetching(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`product?_id=${productId}`, { headers });
            
            if (response?.data?.status === true) {
                const product = response?.data?.data?.products;
                
                setEditedData({
                    _id: product._id,
                    sequenceNumber: product?.sequenceNumber,
                    name: product.name,
                    description: product.description,
                    hsn: product.hsn,
                    taxPercent: product.taxPercent,
                    makingTime: product.makingTime,
                    mrp: product.mrp,
                    status: product?.status,
                    category: product?.category?._id,
                    offerPrice: product?.offerPrice,
                    foodType: product?.foodType,
                    classType: product?.classType,
                    productTagId: product?.productTag?._id,
                    productTagName: product?.productTag?.tagName,
                    isEventProduct: product?.isEventProduct,
                    isFreebieProduct: product?.isFreebieProduct,
                    calories: product?.calories,
                    weight: product?.weight,
                    weightUnit: product?.weightUnit,
                    orderType: product?.orderType,
                    isNewProduct: product?.isNewProduct,
                    productCost: product?.productCost,
                    imageURL: product?.imageURL,
                    coverImageURL: product?.coverImageURL
                });

                setDisplayImagePreview(product?.imageURL || "");
                setCoverImagePreview(product?.coverImageURL || "");

                if (product.keywords && product.keywords.length > 0) {
                    setSelected(product.keywords.split(','));
                }

                let selectedExOutlet = product?.excludedOutletDetails;
                let mappedSelectedExOutlet = selectedExOutlet?.map((m) => ({
                    value: m?._id,
                    label: m?.name
                }));
                setEditedSelectedExOutlets(mappedSelectedExOutlet);

                let selectedNature = product?.productsNature;
                let mappedSelectedNature = selectedNature?.map((m) => m);
                setSelectedNature(mappedSelectedNature);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch product details");
            navigate("/menu/menu-catalog");
        } finally {
            setIsFetching(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("masters/category/get", { headers });
            if (response?.data?.status === true) {
                setCategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch categories");
        }
    };

    const fetchOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("outlet", { headers });
            if (response?.data?.status === true) {
                setOutlets(response?.data?.data?.outlets);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch outlets");
        }
    };

    const fetchProductTagsList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`/product/tags`, { headers });
            if (response?.data?.status === true) {
                setProductTagsList(response?.data?.data?.productTags);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch product tags");
        }
    };

    const searchProductTags = async (text) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`/product/tags?search=${text}`, { headers });
            if (response?.data?.status === true) {
                setProductTags(response?.data?.data?.productTags);
            } else {
                setProductTags([]);
            }
        } catch (error) {
            handleApiError(error, "Failed to search product tags");
        }
    };

    const handleApiError = (error, defaultMessage) => {
        let errorMessage = defaultMessage;
        if (error?.response) {
            if (error.response.status === 400) {
                errorMessage = error.response.data.message || error.response.data.title;
            } else if (error.response.status === 404) {
                errorMessage = error.response.data.message || "Resource not found";
            } else if (error.response.status === 406) {
                errorMessage = error.response.data.error?.message || error.response.data.error;
            } else if (error.response.status === 412) {
                errorMessage = error.response.data.data?.message || error.response.data.error;
            } else if (error.response.status === 500) {
                errorMessage = error.response.data.error || "Server error";
            }
        }
        toast.error(errorMessage);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearchText = (e) => {
        const value = e.target.value;
        setTagText(value);
        setEditedData((prev) => ({ ...prev, productTagName: value, productTagId: null }));
        if (value.length > 1) {
            searchProductTags(value);
        } else {
            setProductTags([]);
        }
    };

    const handleOptionSelect = (selectedOption, selectedName) => {
        setEditedData((prev) => ({ ...prev, productTagName: selectedName, productTagId: selectedOption }));
        setProductTags([]);
        setTagText(selectedName);
    };

    const handleExcludedOutletsChange = (selected) => {
        setEditedSelectedExOutlets(selected);
    };

    const handleDisplayImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/avif', 'image/webp'];
            
            if (!validFormats.includes(file.type)) {
                setImageError('Invalid file format. Only PNG, JPG, JPEG, GIF, WEBP, and AVIF are allowed.');
                return;
            }
            
            setImageError('');
            setDisplayImage(file);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setDisplayImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCoverImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/avif', 'image/webp'];
            
            if (!validFormats.includes(file.type)) {
                setImageError('Invalid file format. Only PNG, JPG, JPEG, GIF, WEBP, and AVIF are allowed.');
                return;
            }
            
            setImageError('');
            setCoverImage(file);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const outletData = outlets.map((outlet) => ({
        value: outlet._id,
        label: `${outlet.name} (${outlet.city})${outlet.status === "COMING_SOON" || outlet.status === "INACTIVE"
            ? ` - ${outlet.status.replace("_", " ")}`
            : ""
        }`,
    }));

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            minHeight: "32px",
            borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,0.25)" : null,
            "&:hover": {
                borderColor: "#0d6efd",
            },
            fontSize: "0.875rem",
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
            fontSize: "0.875rem",
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: "0.875rem",
            padding: "6px 12px",
            backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#e7f1ff" : "white",
            color: state.isSelected ? "white" : "#212529",
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: "#e7f1ff",
            borderRadius: "4px",
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: "#0d6efd",
            fontSize: "0.8125rem",
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: "#0d6efd",
            "&:hover": {
                backgroundColor: "#0d6efd",
                color: "white",
            },
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 1000,
        }),
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selected.length === 0) {
            toast.error("Product keywords is required");
            return;
        }

        setIsLoading(true);

        try {
            const headers = { 
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${accessToken}` 
            };

            let selectedExOutlets = editedSelectedExOutlets?.map((ex) => ex?.value);
            const _prodKeywords = selected.map(val => val.trim()).join(',');

            const formData = new FormData();
            formData.append("_id", editedData._id);
            formData.append("sequenceNumber", editedData.sequenceNumber);
            formData.append("category", editedData.category);
            formData.append("name", editedData.name);
            formData.append("description", editedData.description);
            formData.append("hsn", editedData.hsn);
            formData.append("taxPercent", editedData.taxPercent);
            formData.append("status", editedData.status);
            
            if (editedData.makingTime) {
                formData.append("makingTime", editedData.makingTime);
            }
            
            formData.append("mrp", editedData.mrp);
            formData.append("offerPrice", editedData.offerPrice);
            formData.append("isEventProduct", editedData.isEventProduct);
            formData.append("isFreebieProduct", editedData.isFreebieProduct);
            
            if (editedData?.productTagId) {
                formData.append("productTag[productTagId]", editedData.productTagId);
            }
            formData.append("productTag[productTagName]", editedData.productTagName);
            formData.append("foodType", editedData.foodType);
            formData.append("classType", editedData.classType);
            formData.append("calories", editedData.calories);
            formData.append("weight", editedData.weight);
            formData.append("weightUnit", editedData.weightUnit);
            formData.append("isNewProduct", editedData.isNewProduct);
            formData.append("orderType", editedData.orderType);
            formData.append("keywords", _prodKeywords);
            formData.append("productCost", editedData?.productCost ? editedData?.productCost : 0);

            if (displayImage) {
                formData.append("displayImage", displayImage);
            }
            
            if (coverImage) {
                formData.append("coverPicture", coverImage);
            }

            if (selectedExOutlets.length === 0) {
                formData.append("excludedOutlets", null);
            } else {
                selectedExOutlets.forEach((outlet, index) => {
                    formData.append(`excludedOutlets[${index}]`, outlet);
                });
            }

            if (selectedNature.length === 0 || selectedNature.length === undefined) {
                formData.append("productsNature", null);
            } else {
                selectedNature.forEach((outlet, index) => {
                    formData.append(`productsNature[${index}]`, outlet);
                });
            }

            const response = await axiosInstance.put("product", formData, { headers });

            if (response?.data?.status === true) {
                toast.success("Product updated successfully!");
                navigate(`/menu/menu-catalog`);
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = '';
                if (error.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error;
                }
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <React.Fragment>
                <Header onSkin={setSkin} />
                <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark" : "bg-light"}`}>
                    <div className="container shadow-sm p-4 text-center" style={{ backgroundColor: "white" }}>
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading product details...</p>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark" : "bg-light"}`}>
                <div className="container shadow-sm p-4" style={{ backgroundColor: "white" }}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fw-bold mb-0">Update Product</h4>
                        <Button variant="outline-secondary" size="sm" onClick={() => navigate("/menu/menu-catalog")} className="d-flex align-items-center gap-2">
                            <ArrowLeft size={16} /> Back to Catalog
                        </Button>
                    </div>
                    <hr />

                    <Form onSubmit={handleSubmit}>
                        {/* Image Upload Section - Side by Side */}
                        <Row className="g-4 mb-4">
                            <Col md={6}>
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <div className="bg-primary bg-opacity-10 p-2 rounded">
                                                <ImageIcon className="text-primary" size={18} />
                                            </div>
                                            <h6 className="mb-0 fw-semibold">Display Image</h6>
                                        </div>
                                        <div className="text-center mb-3">
                                            {displayImagePreview ? (
                                                <Image 
                                                    src={displayImagePreview} 
                                                    alt="Display Preview" 
                                                    thumbnail
                                                    style={{ maxHeight: '120px', objectFit: 'contain' }}
                                                    className="border"
                                                />
                                            ) : (
                                                <div className="border rounded-3 p-3 bg-light">
                                                    <ImageIcon size={40} className="text-muted opacity-50" />
                                                    <p className="text-muted small mb-0 mt-2">No image uploaded</p>
                                                </div>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={handleDisplayImageChange}
                                            size="sm"
                                            className="mt-2"
                                        />
                                        <Form.Text className="text-muted small">
                                            Recommended: 500x500px, PNG or JPG
                                        </Form.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            
                            <Col md={6}>
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <div className="bg-success bg-opacity-10 p-2 rounded">
                                                <FileText className="text-success" size={18} />
                                            </div>
                                            <h6 className="mb-0 fw-semibold">Cover Image</h6>
                                        </div>
                                        <div className="text-center mb-3">
                                            {coverImagePreview ? (
                                                <Image 
                                                    src={coverImagePreview} 
                                                    alt="Cover Preview" 
                                                    thumbnail
                                                    style={{ maxHeight: '120px', objectFit: 'contain' }}
                                                    className="border"
                                                />
                                            ) : (
                                                <div className="border rounded-3 p-3 bg-light">
                                                    <FileText size={40} className="text-muted opacity-50" />
                                                    <p className="text-muted small mb-0 mt-2">No image uploaded</p>
                                                </div>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={handleCoverImageChange}
                                            size="sm"
                                            className="mt-2"
                                        />
                                        <Form.Text className="text-muted small">
                                            Recommended: 1200x400px, PNG or JPG
                                        </Form.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            
                            {imageError && (
                                <Col md={12}>
                                    <div className="alert alert-danger py-2 small mb-0">
                                        {imageError}
                                    </div>
                                </Col>
                            )}
                        </Row>

                        {/* Product Details Form - 4 Columns Per Row */}
                        <Row className="g-3">
                            {/* Row 1: Basic Information */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Sequence Number <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter sequence"
                                        name="sequenceNumber"
                                        value={editedData?.sequenceNumber}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Category <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        onChange={handleChange}
                                        name="category"
                                        value={editedData?.category}
                                    >
                                        <option value="">Select category</option>
                                        {categoryList.map((rm) => (
                                            <option key={rm._id} value={rm._id}>
                                                {rm.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Product Name <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Enter product name"
                                        name="name"
                                        value={editedData?.name}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Class Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        onChange={handleChange}
                                        name="classType"
                                        value={editedData?.classType}
                                    >
                                        <option value="">Select class</option>
                                        <option value="FOOD">FOOD</option>
                                        <option value="BEVERAGE">BEVERAGE</option>
                                        <option value="COMBOS">COMBOS</option>
                                        <option value="DESSERTS">DESSERTS</option>
                                        <option value="EXTRAS">EXTRAS</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Row 2: Product Details */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        HSN Code <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Enter HSN"
                                        name="hsn"
                                        value={editedData?.hsn}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Calories <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter calories"
                                        name="calories"
                                        value={editedData?.calories}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Weight <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter weight"
                                        name="weight"
                                        value={editedData?.weight}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Weight Unit <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        onChange={handleChange}
                                        name="weightUnit"
                                        value={editedData?.weightUnit}
                                    >
                                        <option value="">Select unit</option>
                                        <option value="KG">Kilograms (KG)</option>
                                        <option value="GMS">Grams (GMS)</option>
                                        <option value="LITRES">Litres (LTR)</option>
                                        <option value="ML">Millilitres (ML)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Row 3: Pricing and Tax */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Tax Percent <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter tax %"
                                        name="taxPercent"
                                        value={editedData?.taxPercent}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Order Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="orderType"
                                        value={editedData.orderType}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select type</option>
                                        <option value="ALL">ALL</option>
                                        <option value="DELIVERY">DELIVERY</option>
                                        <option value="PICKUP">PICK-UP</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Making Time (mins) <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter minutes"
                                        name="makingTime"
                                        value={editedData?.makingTime}
                                        onChange={handleChange}
                                        disabled={editedData?.orderType === "DELIVERY"}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Product Cost (₹)
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter cost"
                                        name="productCost"
                                        value={editedData?.productCost}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Row 4: Pricing and Status */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        MRP (₹) <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        step="any"
                                        placeholder="Enter MRP"
                                        name="mrp"
                                        disabled
                                        value={editedData?.mrp}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Offer Price (₹) <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        disabled
                                        step="any"
                                        placeholder="Enter offer"
                                        name="offerPrice"
                                        value={editedData?.offerPrice}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="status"
                                        value={editedData.status}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select status</option>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Food Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="foodType"
                                        value={editedData.foodType}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select type</option>
                                        <option value="VEG">VEG</option>
                                        <option value="NON-VEG">NON-VEG</option>
                                        <option value="VEGAN">VEGAN</option>
                                        <option value="VEG/VEGAN">VEG/VEGAN</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Row 5: Toggle Options */}
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Freebie Product
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="isFreebieProduct"
                                        value={editedData.isFreebieProduct}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="true">YES</option>
                                        <option value="false">NO</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        New Badge
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="isNewProduct"
                                        value={editedData.isNewProduct}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Outlet Type
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        name="isEventProduct"
                                        value={editedData.isEventProduct}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="false">Regular Outlet</option>
                                        <option value="true">Event Outlet</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Product Tag
                                    </Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            placeholder="Search or add tag"
                                            name="productTagName"
                                            onChange={handleSearchText}
                                            value={tagText || editedData?.productTagName}
                                        />
                                        {productTags && productTags.length > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                width: '100%',
                                                zIndex: 1000,
                                                backgroundColor: 'white',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '0.375rem',
                                                marginTop: '0.25rem',
                                                maxHeight: '150px',
                                                overflowY: 'auto',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                                {productTags.map((prod) => (
                                                    <div
                                                        key={prod._id}
                                                        style={{
                                                            padding: '0.375rem 0.75rem',
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid #f8f9fa',
                                                            fontSize: '0.875rem'
                                                        }}
                                                        onClick={() =>
                                                            handleOptionSelect(prod._id, prod.tagName)
                                                        }
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    >
                                                        {prod.tagName}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>

                            {/* Row 6: Multi-select Fields */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Excluded Outlets
                                    </Form.Label>
                                    <Select
                                        isMulti
                                        components={animatedComponents}
                                        value={editedSelectedExOutlets}
                                        onChange={handleExcludedOutletsChange}
                                        options={outletData}
                                        styles={customStyles}
                                        placeholder="Select excluded outlets..."
                                        closeMenuOnSelect={false}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Product Keywords <span className="text-danger">*</span>
                                        <small className="text-muted ms-2">(Press Enter after each)</small>
                                    </Form.Label>
                                    <TagsInput
                                        value={selected}
                                        onChange={setSelected}
                                        name="keywords"
                                        placeHolder="Type and press enter"
                                    />
                                </Form.Group>
                            </Col>

                            {/* Row 7: Nature of Product and Description */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Nature of Product
                                        <small className="text-muted ms-2">(Press Enter after each)</small>
                                    </Form.Label>
                                    <TagsInput
                                        value={selectedNature}
                                        onChange={setSelectedNature}
                                        name="productNature"
                                        placeHolder="Add nature tags"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold text-secondary mb-1">
                                        Description <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Enter detailed product description..."
                                        name="description"
                                        value={editedData?.description}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Form Actions */}
                        <Row className="mt-4 pt-3 border-top">
                            <Col className="d-flex justify-content-end gap-2">
                                <Button 
                                    variant="light" 
                                    size="sm"
                                    onClick={() => navigate("/menu/menu-catalog")}
                                    className="d-flex align-items-center gap-2 px-4"
                                >
                                    <X size={16} /> Cancel
                                </Button>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    type="submit" 
                                    disabled={isLoading}
                                    className="d-flex align-items-center gap-2 px-4"
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} /> Update Product
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default UpdateMenu;